/**
 * Branded Types - Type-safe ID wrappers using Zod 4.1.9
 *
 * Zod 4 API: z.string().brand("BrandName") - takes string argument
 */
import { z } from 'zod';
import {
  AcademicYear,
  UserId,
  UnixTimestamp,
  createBrandedString,
  createBrandedUuid,
  createBrandedUuidLoose,
  dateToUnix,
  nowUnix,
  unixToDate,
} from '@triedge/domain-core';

export {
  AcademicYear,
  UserId,
  UnixTimestamp,
  createBrandedString,
  createBrandedUuid,
  createBrandedUuidLoose,
  dateToUnix,
  nowUnix,
  unixToDate,
};

// ============================================================================
// Entity IDs (UUIDs)
// ============================================================================

export const ApplicantId = createBrandedUuidLoose('ApplicantId');
export type ApplicantId = z.infer<typeof ApplicantId>;

export const FinancialAidApplicationId = createBrandedUuid('FinancialAidApplicationId');
export type FinancialAidApplicationId = z.infer<typeof FinancialAidApplicationId>;

export const FinancialAidMetricsId = createBrandedUuid('FinancialAidMetricsId');
export type FinancialAidMetricsId = z.infer<typeof FinancialAidMetricsId>;

export const ScholarshipRecommendationId = createBrandedUuid('ScholarshipRecommendationId');
export type ScholarshipRecommendationId = z.infer<typeof ScholarshipRecommendationId>;

export const ReviewFlagId = createBrandedUuid('ReviewFlagId');
export type ReviewFlagId = z.infer<typeof ReviewFlagId>;

// ============================================================================
// Human-Readable Codes (Zod 4 API)
// ============================================================================

/**
 * Financial Aid Application code format: FA-YYYY-NNNNN
 * Example: FA-2025-00001
 */
export const FinancialAidCode = z.string()
  .regex(/^FA-\d{4}-\d{5}$/, 'Financial Aid code format must match FA-YYYY-NNNNN')
  .brand('FinancialAidCode');
export type FinancialAidCode = z.infer<typeof FinancialAidCode>;
